'use client';

import { flattenCategoryTree } from '@/features/categories/categoryTreeFilters';
import {
  isShopPath,
  setShopFocusIntent,
  SHOP_FOCUS_FILTER_BAR,
} from '@/features/shop/shopFocusIntent';
import { buildShopPath } from '@/features/shop/shopQuery';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useMemo, useState } from 'react';
import { FaTimes } from 'react-icons/fa';

import ClearFilters from '@/features/primitives/ClearFilters';
import SearchInput from '@/features/search-input/SearchInput.jsx';
import '../../scss/_category-tree-offcanvas.scss';
import CategoryFilter from './components/CategoryFilter.jsx';
import './filterBar.scss';

const SEARCH_STORAGE_KEY = 'search:lastKeyword';

export default function FilterBar({
  categoriesTree = [],
  pathname = '',
  onSubmit,
  onNavigate,
  filterMessages = {},
}) {
  const [removingFilter, setRemovingFilter] = useState(null);
  const router = useRouter();
  const searchParams = useSearchParams();
  const currentPathname = pathname || usePathname() || '/';
  const fm = (key, fallback) =>
    typeof filterMessages[key] === 'string' ? filterMessages[key] : fallback;

  const selectedSlugs = searchParams.getAll('category').filter(Boolean);
  const keyword = searchParams.get('keyword') || '';
  const sort = searchParams.get('sort') || 'newest';
  const minRating = searchParams.get('minRating') || '';
  const inStockOnly = searchParams.get('inStock') === 'true';
  const activeFilterCount =
    (keyword ? 1 : 0) +
    selectedSlugs.length +
    (sort !== 'newest' ? 1 : 0) +
    (minRating ? 1 : 0) +
    (inStockOnly ? 1 : 0);
  const hasFilters =
    Boolean(keyword) ||
    selectedSlugs.length > 0 ||
    sort !== 'newest' ||
    Boolean(minRating) ||
    inStockOnly;
  const selectedCategories = useMemo(() => {
    const flattened = flattenCategoryTree(categoriesTree || []);
    return selectedSlugs
      .map((slug) => flattened.find((entry) => entry.slug === slug))
      .filter(Boolean);
  }, [categoriesTree, selectedSlugs]);

  const navigateTo = (updates) => {
    if (isShopPath(currentPathname)) {
      setShopFocusIntent(SHOP_FOCUS_FILTER_BAR);
    }

    router.push(buildShopPath(searchParams, updates, currentPathname), {
      scroll: false,
    });
    onNavigate?.();
  };

  const handleRemove = (slug = null) => {
    const target = slug || '__keyword__';
    setRemovingFilter(target);

    requestAnimationFrame(() => {
      setTimeout(() => {
        if (slug) {
          navigateTo({
            category: selectedSlugs.filter((entry) => entry !== slug),
            page: 1,
          });
        } else {
          try {
            window.localStorage.removeItem(SEARCH_STORAGE_KEY);
          } catch {
            // Ignore storage access failures in restricted environments.
          }
          navigateTo({ keyword: null, page: 1 });
        }
        setRemovingFilter(null);
      }, 250);
    });
  };

  const hasVisibleFilterChips =
    Boolean(keyword) || selectedCategories.length > 0;

  const activeFilterLabel =
    activeFilterCount === 1
      ? fm('activeOne', '{{count}} active filter')
      : fm('activeOther', '{{count}} active filters');

  const handleClearFilters = () => {
    setRemovingFilter(null);

    try {
      window.localStorage.removeItem(SEARCH_STORAGE_KEY);
    } catch {
      // Ignore storage access failures in restricted environments.
    }

    navigateTo({
      keyword: null,
      category: null,
      sort: null,
      minRating: null,
      inStock: null,
      page: 1,
    });
  };

  return (
    <section
      id="filter-bar"
      className="filter-bar sticky-top p-3 pt-4 px-md-4 z-3"
      tabIndex={-1}
    >
      <div className="search-container mb-3 w-100">
        <SearchInput
          initialKeyword={keyword}
          targetPathname={currentPathname}
          searchItem="products"
          onSubmit={onSubmit}
          placeholder={fm('searchInputPlaceholder', 'Search products...')}
          searchButtonLabel={fm('searchButton', 'Search')}
          clearLabel={fm('clearSearch', 'Clear')}
          searchStatusTemplate={fm(
            'searchStatusSearching',
            'Searching for {{keyword}}.',
          )}
          clearedStatusMessage={fm('searchStatusCleared', 'Search cleared.')}
        />
      </div>

      <div className="category-filter-container d-flex align-items-center gap-2 flex-wrap mb-2 mb-md-3">
        <CategoryFilter
          categoriesTree={categoriesTree}
          pathname={currentPathname}
          onNavigate={onNavigate}
          filterMessages={filterMessages}
        />
      </div>

      {hasFilters ? (
        <div className="filter-bar__summary d-flex align-items-center justify-content-between gap-2 flex-wrap mb-2">
          <span className="filter-bar__summary-count">
            {activeFilterLabel.replace('{{count}}', String(activeFilterCount))}
          </span>
          <ClearFilters
            handleClear={handleClearFilters}
            extraClasses="filter-bar__clear-trigger"
            label={fm('clearAction', 'Clear all')}
            tooltip={fm('clearAction', 'Clear all')}
          />
        </div>
      ) : null}

      <div className="selected-tags d-flex justify-content-start align-items-center">
        <div className="active-filters flex-wrap align-items-center flex-grow-1 pt-1 d-flex">
          {hasVisibleFilterChips ? (
            <div className="d-flex flex-wrap mb-msm-3">
              {selectedCategories.map((category) => {
                const removeCategoryLabel = fm(
                  'removeCategory',
                  'Remove category {{name}}',
                ).replace('{{name}}', category.name);

                return (
                  <button
                    key={category.slug}
                    type="button"
                    className={`filter-badge clickable-badge mb-2 fade-out-filter me-2 ${
                      removingFilter === category.slug ? 'removing' : ''
                    }`}
                    onClick={() => handleRemove(category.slug)}
                    aria-label={removeCategoryLabel}
                  >
                    <span className="text-primary d-flex align-items-center gap-1">
                      {category.icon ? (
                        <span
                          className="category-icon"
                          role="img"
                          aria-label={`${category.name} icon`}
                        >
                          {category.icon}
                        </span>
                      ) : null}
                      <span>{category.name}</span>
                      <FaTimes size={14} aria-hidden="true" />
                    </span>
                  </button>
                );
              })}

              {keyword ? (
                <button
                  type="button"
                  className={`filter-badge clickable-badge mb-2 fade-out-filter ${
                    removingFilter === '__keyword__' ? 'removing' : ''
                  }`}
                  onClick={() => handleRemove()}
                  aria-label={fm(
                    'removeKeyword',
                    'Remove keyword {{keyword}}',
                  ).replace('{{keyword}}', keyword)}
                >
                  <span className="d-flex align-items-center gap-1 text-warning">
                    <span
                      className="category-icon"
                      role="img"
                      aria-label={fm('keywordLabel', 'Keyword')}
                    >
                      🧭
                    </span>
                    <span>{keyword}</span>
                    <FaTimes size={14} aria-hidden="true" />
                  </span>
                </button>
              ) : null}
            </div>
          ) : (
            <span className="no-filters fst-italic fade-in-fast w-100 text-md-center fs-8">
              {fm('noFilters', 'No filters applied')}
            </span>
          )}
        </div>
      </div>
    </section>
  );
}
